import { Injectable } from '@angular/core';
import { Whiteboard } from '../models/whiteboard';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { GalleryViewOrderedComponent } from '../components/widgets/gallery-widget/gallery-ordered-view/gallery-view-ordered.component';
import { AwallComponentEvent } from '../models/awallComponentEvent';
import { GalleryWidgetComponent } from '../components/widgets/gallery-widget/gallery-widget.component';
import { MessageService } from './message.service';
import { SettingsService } from './settings.service';
import { SynchronizationService } from './synchronization.service';
import { ConfigLoaderService } from '../config/configloader.service';
import { GalleryService } from './gallery.service';
import { WBUtils } from '../components/widgets/whiteboard-widget/util/WBUtils';
import { CommunicationService } from './communication.service';
import { Image } from '../models/image';
import { TaskboardViewComponent } from '../actionviews/taskboard-view/taskboard-view.component';
import { AttachmentType } from '../models/enums/attachmentType';
import { format } from 'date-fns';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  })
};


@Injectable()
export class WhiteboardIOService {
  private whiteboards: any;
  private pyWallBaseUrl: string;
  private currentTempFileName: string;
  private currentProjectWhiteboardFilename: string;

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private settingsService: SettingsService,
    private synchronization: SynchronizationService,
    private configService: ConfigLoaderService,
    private galleryService: GalleryService,
    private communicationService: CommunicationService) {

    this.pyWallBaseUrl = `${configService.settings.pyWallServer.url}`;
    this.currentProjectWhiteboardFilename = '';
    this.currentTempFileName = '';
  }

  getCurrentTempFileName(): string {
    return this.currentTempFileName;
  }

  setCurrentTempFileName(tempFileName: string) {
    this.currentTempFileName = tempFileName;
  }

  getCurrentProjectWhiteboard(): string {
    return this.currentProjectWhiteboardFilename;
  }

  setCurrentProjectWhiteboard(projectWhiteboard: string) {
    this.currentProjectWhiteboardFilename = projectWhiteboard;
  }

  getWhiteboards(): any {
    return this.whiteboards;
  }

  /**
     * Loads all whiteboards for the current project from the database. If there is data, onList()
     * gets executed, otherwise onEmpty()
     */
  loadProjectWhiteboards(onList: (wbs) => void, onEmpty: (s) => void) {
    const projectKey = this.settingsService.getProjectName();
    const targetUrl = this.pyWallBaseUrl + 'whiteboards?projectId=' + projectKey;

    this.http.get<Whiteboard[]>(targetUrl).subscribe(response => {
      const projectWiteboards = response['whiteboards'];

      if (projectWiteboards && projectWiteboards.length > 0) {
        this.whiteboards = projectWiteboards;
        onList(this.whiteboards);
      } else {
        onEmpty(this.currentProjectWhiteboardFilename);
      }
    });
  }


  /**
   * Saves the current opened whiteboard and loads the new list afterwards.
   * The callback functions are passed as arguments to the loading function.
   *
   * @param emitRealtimeEvent if true sends a realtimeevent about the save to connected clients
   */
  saveCanvasContentInProject(
    emitRealtimeEvent: boolean,
    onList: (wbs) => void,
    onEmpty: (s) => void,
    showIcon: () => void,
    canvasDataJSON: string): void {

    const projectKey = this.settingsService.getProjectName();
    const targetUrl = this.pyWallBaseUrl + 'whiteboards/';
    this.messageService.sendDebug(`Whiteboard saveCanvas called on project ${projectKey}`);

    if (this.currentProjectWhiteboardFilename.length === 0) {
      this.createNewProjectFileName();
      WBUtils.whiteboardStateLabel.html(this.currentProjectWhiteboardFilename);
    }

    const whitboardData = {
      'projectId': projectKey,
      'jsonData': canvasDataJSON,
      'name': this.currentProjectWhiteboardFilename,
      'user': 'aWall',
      'task': ''
    };

    this.http.post<Whiteboard>(targetUrl, whitboardData, httpOptions).subscribe({
      next: () => {
        showIcon();
        this.loadProjectWhiteboards(onList, onEmpty);
        if (emitRealtimeEvent) {
          this.synchronization.sendRealTimeEvent(`WhiteboardWidget${WBUtils.widgetName}SaveWBProject`);
        }
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  /**
     * Saves an image in the gallery by calling the function of the GalleryService.
     */
  saveWhiteboardAsImageInGallery(canvas: any) {
    canvas.renderAll();

    const projectKey = this.settingsService.getProjectName();
    const file = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
    const fileName = 'whiteboard' + projectKey + '-' + Date.now() + '.png';

    this.galleryService.addImage(projectKey, file as string, fileName, () => {
      this.communicationService.sendData(
        GalleryViewOrderedComponent.name, new AwallComponentEvent('reloadGallery', 1));
      this.communicationService.sendData(
        GalleryWidgetComponent.name, new AwallComponentEvent('reloadGallery', 1));
    },
    error => {
      console.log('Error saving whiteboard as gallery-image: ', error);
    });
  }

  /**
     * Send an event about the dropped image to the Components how need to know and react to this event.
     */
  onDropCanvasToIssue(issueId: number) {
    const fileName = this.currentTempFileName;
    const image = new Image(this.getCurrentTempFileId(), fileName);
    image.type = AttachmentType.WhiteboardImage;

    const eventPayload = { 'issueId': issueId, 'image': image };
    const componentEvent = new AwallComponentEvent('image-dropped', eventPayload);
    this.communicationService.sendData(TaskboardViewComponent.name, componentEvent);
  }

  createNewProjectFileName() {
    const projectKey = this.settingsService.getProjectName();
    const timestamp: string = format(new Date(), 'YYYY-MM-d-hh-mm-ss');
    this.currentProjectWhiteboardFilename = `whiteboard${projectKey}-${timestamp}.swb`;
  }

  createNewTempFileName() {
    const projectKey = this.settingsService.getProjectName();
    const timeStamp = (new Date()).getTime();
    this.currentTempFileName = `temp${projectKey}-${timeStamp}.png`;
  }

  /* Gets a numberic id for the current tempFileName by extracting the number-only-timestamp. */
  getCurrentTempFileId(): number {
    if (this.currentTempFileName) {
      return Number(this.currentTempFileName.replace(/\D/g, ''));
    } else {
      return null;
    }
  }

  /**
     * Loads a whiteboard from a specified url. Adds a url argument to prevent loading chached files. Callback function
     * is executed afterwards.
     */
  loadWhiteboard(baseUrl: string, url: string, forceSync: boolean, onLoadBoard: (f, s, u, r) => void, currentProjectWB) {
    const urlParts = url.split('/');
    this.currentProjectWhiteboardFilename = urlParts[urlParts.length - 1];
    const fileUrl = baseUrl.replace('/projects', '/') + url + '?v=' + Date.now();
    const raw = new XMLHttpRequest();
    raw.open('GET', fileUrl, false);
    raw.onreadystatechange = function () {
      if (raw.readyState === 4) {
        if (raw.status === 200 || raw.status === 0) {
          onLoadBoard(forceSync, currentProjectWB, url, raw);
        }
      }
    };
    raw.send(null);
  }

  /**
     * Saves the dynamic rendered image from the dragOutInteraction to specified url with specified
     * name. Callback function is executed afterwards.
     */
  saveDraggablePreviewAsImage(image: any, fileName: any) {
    this.messageService.sendDebug('WhiteboardService save draggable preview as image');
    const projectKey = this.settingsService.getProjectName();
    const targetUrl = this.pyWallBaseUrl + 'whiteboards/';
    const imageData = {
      'projectId': projectKey,
      'binaryData': image.split(',')[1],
      'name': fileName,
      'user': 'aWall',
      'task': ''
    };

    this.http.post<Whiteboard>(targetUrl, imageData, httpOptions).subscribe({
      error: (error) => {
        console.error('Error saving whiteboard preview as image:', error);
      }
    });
  }

  deleteWhiteboard(targetUrl: string, projectKey: string, onSuccess: any, onError: any): void {
    this.messageService.sendDebug(`WhiteboardService delete WB called on project ${projectKey}`);
    this.http.delete(targetUrl, { responseType: 'text', }).subscribe({
      next: (response) => {
        onSuccess(response);
      },
      error: (error) => {
        onError(error);
      }
    });
  }


}
